import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Mail, MessageSquare, Send, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { submitContact } from '../lib/api';

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast({ title: 'Message envoyé !', description: 'Notre équipe vous recontactera sous 24h ouvrées.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || "Impossible d'envoyer le message. Réessayez.", variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Contact — conseillers formation & financement"
        description="Contactez Medelia Santé pour une question sur une formation, le financement (DPC, FIFPL, OPCO, CPF) ou un partenariat. Réponse en général sous 24h ouvrées."
        canonicalPath="/contact"
      />
      <Navbar />
      <main className="safe-pt-nav">
        <section className="py-16 bg-gradient-to-br from-teal-50/60 via-white to-emerald-50/40">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
            <span className="text-teal-600 text-sm font-bold uppercase tracking-widest">Contactez-nous</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
              Parlons de votre formation
            </h1>
            <p className="text-slate-600 mt-4 max-w-xl mx-auto">Une question ? Un projet de formation ? Notre équipe de conseillers est là pour vous accompagner.</p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Envoyez-nous un message</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jean Dupont" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jean@exemple.fr" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Sujet *</Label>
                    <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                      <SelectTrigger><SelectValue placeholder="Choisissez un sujet" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="formation">Information formation</SelectItem>
                        <SelectItem value="financement">Question financement</SelectItem>
                        <SelectItem value="partenariat">Partenariat</SelectItem>
                        <SelectItem value="support">Support technique</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea id="message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Décrivez votre demande..." />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="w-full min-h-11 bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20 touch-manipulation sm:w-auto"
                >
                  <Send className="w-4 h-4 mr-2" /> {loading ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </div>

            <div className="space-y-4">
              {[
                { icon: Mail, title: 'Email', value: 'contact@medelia-sante.com', sub: 'Réponse sous 24h' },
                { icon: Clock, title: 'Horaires', value: 'Lun-Ven: 9h - 18h', sub: 'Fermé le week-end' },
              ].map((it, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-teal-300 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                      <it.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{it.title}</p>
                      <p className="font-bold text-slate-900 mt-0.5">{it.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{it.sub}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-2xl p-6">
                <MessageSquare className="w-7 h-7 mb-3" />
                <h3 className="font-bold text-lg mb-1">Besoin d'une réponse rapide ?</h3>
                <p className="text-sm text-teal-50">Notre équipe commerciale est disponible pour un appel découverte gratuit.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
